import { Router, ActivatedRoute } from '@angular/router';
import { Subject, Subscription, timer } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { Component, OnInit, ViewChild, Input, OnDestroy } from '@angular/core';
import { LazyLoadEvent, SelectItem, MessageService, ConfirmationService, Table } from 'primeng';
import { ICustomerInvoice } from '../../interfaces/customer-invoice';
import { ICustomerInvoiceFilter } from '../../interfaces/customer-invoice-filter';
import { IPageResult } from '../../interfaces/page-result';
import { ISystemSettings } from '../../interfaces/system-settings';
import { BillingService } from '../../services';
import { CustomerInvoiceService } from '../../services';
import { FileStorageService } from '../../services';
import { ReportService } from '../../services';
import { SettingsService } from '../../services';
import { flatten } from '../../utils/flatten';
import { Message } from '../../classes/Message';
import { InvoiceProcessMode } from '../../classes/invoice-process-mode';
import { SystemSetting } from '../settings/settings';
import { BaseReportComponent } from '../reports/base-report.component';
import { ServerSentEventService } from '../../services';
import { debounceTime, filter, take, takeUntil } from 'rxjs/operators';
import { OrganisationInvoiceReportService } from 'src/app/services/organisation-invoice-report.service';

export enum InvoicePeriod {
  All = 0, // default
  Monthly = 1,
  Weekly = 2,
}

@Component({
  selector: 'customers-invoices-grid',
  templateUrl: './customers-invoices-grid.component.html',
  styleUrls: ['./customers-invoices-grid-component.scss'],
})
export class CustomersInvoicesGridComponent
  extends BaseReportComponent<any, any, any>
  implements OnInit, OnDestroy
{
  public page: IPageResult<ICustomerInvoice> = {
    items: [],
    total: 0,
  };

  private manageMode_customer = 1;
  private manageMode_vme = 2;

  public isLoaded: boolean = false;
  public filterVal: ICustomerInvoiceFilter;
  public filter: Subject<ICustomerInvoiceFilter>;
  public selectedInvoice = null;

  public customers: SelectItem[] = [this.billingService.customersDefSelect];

  //testing
  public isTestMode: boolean;
  public plannedRunDay: Date = new Date();
  public emulateToday: Date = new Date();
  public runDelayedDays: number = 0;
  public tlinesMax: number = 5000;
  public transactionsFrom: Date = new Date();
  public transactionsDays: number = 1;
  public transactionsCustomerId: number;
  public transactionsCustomers: SelectItem[] = [];
  public customerInvoiceProcess: InvoiceProcessMode;
  public supplierWetRunPassPhrase: string;
  public billResults: any[];
  public xeroResults: any[];
  public showProgressBar: boolean;
  public invoicePeriod: number = 0;
  //

  private _manageMode: number;
  @Input('manageMode')
  set manageMode(value: number) {
    this._manageMode = value;
  }

  get manageMode(): number {
    return this._manageMode;
  }

  private systemSettings: ISystemSettings;

  public get isCustomerMode(): boolean {
    return this.manageMode == this.manageMode_customer;
  }

  public get isVmeMode(): boolean {
    return this.manageMode == this.manageMode_vme;
  }

  public invoicePeriodSelect: { label: string; value: string }[];
  private processInvoiceSubscription: Subscription;

  constructor(
    reportService: ReportService<any, any>,
    private customerInvoiceService: CustomerInvoiceService,
    private billingService: BillingService,
    public messageService: MessageService,
    private router: Router,
    private fileStorageService: FileStorageService,
    private route: ActivatedRoute,
    private settingsService: SettingsService,
    private confirmationService: ConfirmationService,
    private serverSentEventService: ServerSentEventService,
    private organisationInvoiceReportService: OrganisationInvoiceReportService,
  ) {
    super(reportService, false);
    this.customerInvoiceProcess = new InvoiceProcessMode();
    this.invoicePeriodSelect = Object.keys(InvoicePeriod)
      .filter((e) => !isNaN(+e))
      .map((key) => ({
        value: key,
        label: InvoicePeriod[key],
      }));
  }

  ngOnInit() {
    this.isTestMode = this.route.snapshot.params['test'] == '1';
    this.initDefaultFilter();

    var datesRange = this.billingService.getLastMonthRange();
    this.filterVal.filters.from = { value: datesRange.from };
    this.filterVal.filters.to = { value: datesRange.to };

    this.filter = new Subject<ICustomerInvoiceFilter>();

    // subscribe to filter changes with a debounce time
    this.filter.pipe(debounceTime(500)).subscribe((f) => {
      this.load(f);
      this.filterVal = f;
    });

    // set default filter
    this.filter.next(this.filterVal);

    //---- testing ----
    this.billingService.bindCustomers().then((res) => {
      this.customers = res;
      this.transactionsCustomers = res.slice(1, res.length);
      this.transactionsCustomerId = this.transactionsCustomers[0].value;
    });

    this.settingsService.GetInvoiceProcessPassPhrase(1).then((data: SystemSetting) => {
      if (data) this.customerInvoiceProcess.systemInfo.passPhrase = data.value;
    });
  }

  initDefaultFilter() {
    this.filterVal = {
      first: 0,
      rows: 10,
      sortField: 'CustomerName',
      sortOrder: 1,
      filters: {
        customerId: { value: null },
        from: { value: null },
        to: { value: null },
      },
    };
  }

  load(filter: ICustomerInvoiceFilter) {
    this.customerInvoiceService.getPage(flatten(filter)).then((result) => {
      this.page.items = result.items;
      this.page.total = result.total;
      this.isLoaded = true;

      //recovering item's selection if its field(s) has been changed
      if (this.selectedInvoice != null) {
        var foundPageItem = this.page.items.find((o) => o.id == this.selectedInvoice.id);

        if (foundPageItem != null) this.selectedInvoice = foundPageItem;
      }
      //
    });
  }

  onGridLazyLoad(event: LazyLoadEvent) {
    this.filterVal.first = event.first;
    this.filterVal.rows = event.rows;
    this.filterVal.sortField = event.sortField;
    this.filterVal.sortOrder = event.sortOrder;
    // map grid lazy load event to filter
    this.filter.next(this.filterVal);
  }

  clearFilter(dt: Table) {
    dt.sortField = '';
    dt.rows = 10;
    this.initDefaultFilter();
    this.filter.next(this.filterVal);

    this.selectedInvoice = null;
  }

  search() {
    this.filterVal.first = 0; //fix error - when you set page > 1 and filter, then data not display
    this.filter.next(this.filterVal);

    this.selectedInvoice = null;
  }

  processInvoices() {
    this.billResults = null;
    this.xeroResults = null;
    this.enableProgressBar();
    this.processInvoiceSubscription = this.billingService
      .processCustomerInvoices(
        this.plannedRunDay,
        this.runDelayedDays,
        this.tlinesMax,
        this.customerInvoiceProcess.isDryRun,
        this.invoicePeriod,
      )
      .pipe(
        map((res) => JSON.parse(res)),
        tap({
          next: ({ billResults }) => {
            this.messageService.add(Message.Success('Customer invoices processed'));
            this.customerInvoiceProcess.isDryRun = true;
            this.hideProgressBar();
            this.search();
          },
          error: (err) => {
            this.hideProgressBar();
            console.log(err);
            this.messageService.add(
              Message.Error(`Error processing customer invoice`, 'Customer Invoice process Error'),
            );
            this.customerInvoiceProcess.isDryRun = true;
          },
        }),
      )
      .subscribe(({ billResults, xeroResults }) => {
        this.billResults = billResults;
        this.xeroResults = xeroResults;
      });
  }

  confirmInvoiceProcess(message) {
    this.confirmationService.confirm({
      message: message,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      key: 'customer-invoice-dialog',
      accept: () => {
        this.customerInvoiceProcess.isDryRun = !this.customerInvoiceProcess.isDryRun;
      },
      reject: () => {},
    });
  }

  verifyRunMode($this) {
    var $thisInputText = $this.target.value;
    $thisInputText =
      $thisInputText != '' && $thisInputText != null ? $thisInputText.trim() : $thisInputText;
    var verificationCheck = this.customerInvoiceProcess.systemInfo.passPhrase;
    // modify the value of run mode on the form
    if ($thisInputText === verificationCheck) {
      this.confirmInvoiceProcess(this.customerInvoiceProcess.invoiceWetRunMessage);
      $this.target.value = '';
    }
    // reset the value every other time
  }

  removeXeroData() {
    this.billingService.removeXeroData().then(() => {
      this.messageService.add(Message.Success(`Done`, ''));
      this.search();
    });
  }

  removeInvoices() {
    this.billingService.removeCustomerInvoices().then(() => {
      this.messageService.add(Message.Success(`Done`, ''));
      this.search();
    });
  }

  removeInvoice(id) {
    this.billingService.removeCustomerInvoice(id).then(() => {
      this.messageService.add(Message.Success(`Done`, ''));
      this.search();
    });
  }

  generateTransactions() {
    this.billingService
      .generateCustomerTransactions(
        this.transactionsCustomerId,
        this.transactionsFrom,
        this.transactionsDays,
      )
      .then((res) => {
        this.messageService.add(
          Message.Success(`Records created: ${res}`, 'Generate transactions'),
        );
      })
      .catch(() => {
        this.messageService.add(Message.Error('', `Generate transactions error.`));
      });
  }

  openPdf(objectKey) {
    this.fileStorageService.openSourceInTab(objectKey);
  }

  runAll() {
    this.billingService
      .processAll(this.emulateToday)
      .then((res) => {
        this.messageService.add(Message.Success(`Run All completed`, 'Run All completed'));
      })
      .catch(() => {
        //this.messageService.add(Message.Error('', `Run All error.`));
      });
  }

  downloadCustomerReport() {
    this.reportService.url = '/api/report/customerInvoice';
    this.reportService.downloadReport(this.filterVal);
  }

  enableProgressBar() {
    this.showProgressBar = true;
  }

  hideProgressBar() {
    this.releaseConnection();
    this.showProgressBar = false;
  }

  releaseConnection() {
    const maxExecutionWait = timer(300000); // wait for operation to complete within 5 minutes

    const source = timer(2500, 5000); // wait 2.5 secs then every other 5 secs

    source
      .pipe(mergeMap(() => this.billingService.isProcessInUse()))
      .pipe(
        filter((x) => x == false),
        take(1),
        takeUntil(maxExecutionWait),
      )
      .subscribe(
        (res) => {},
        (err) => {},
        () => {
          this.serverSentEventService.closeServerEvent();
          this.showProgressBar = false;
        },
      );
  }

  generateCSVReport(invoiceReport) {
    this.showProgressBar = true;
    var report = this.organisationInvoiceReportService.downloadCustomerReportToCSV(
      invoiceReport.id,
      false,
    );
    report.finally(() => {
      this.showProgressBar = false;
    });
  }
  generateAdhocCSVReport = (adHocInvoiceReport) => {
    this.showProgressBar = true;
    var report = this.organisationInvoiceReportService.downloadCustomerReportToCSV(
      0,
      true,
      adHocInvoiceReport.transactionIds,
    );
    report.finally(() => {
      this.showProgressBar = false;
    });
  };

  ngOnDestroy(): void {
    if (this.processInvoiceSubscription) this.processInvoiceSubscription.unsubscribe();
  }

  async downloadOrderLevelInvoice($event: MouseEvent, item: any) {
    $event.preventDefault();

    // getting a cors error trying to download from S3, so using this QAD workaround for now
    const filename = `${item.xeroInvoiceNumber}_order-level_invoices.zip`;
    const link = document.createElement('a');
    link.href = item.orderLevelInvoiceURL;
    link.download = filename; // not working for some reason
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // CORS error
    // const file = await this.billingService.downloadFileFromServer(item.orderLevelInvoiceURL, filename);
    // console.log("File result: ", file)
  }

  async generateOrderLevelInvoice($event: MouseEvent, item: any) {
    $event.preventDefault();

    try {
      item.isBusy = true;
      const PresignedUrl = await this.billingService.generateOrderLevelInvoices(item.id);
      item.orderLevelInvoiceURL = PresignedUrl.url;
    } catch (error) {
      console.error('Failed to generate invoices:', error);
      this.messageService.add(Message.Error('', `Generate order-level invoices error.`));
    } finally {
      item.isBusy = false;
    }
  }
}
